<template>
  <!-- State: No Tasks | No Results | Error -->
  <div class="h-full flex justify-center" v-show="state">
    <slot name="state" />
  </div>

  <!-- Data: List -->
  <div v-show="!state" class="h-full flex grow flex-col overflow-y-auto">
    <AccordionRoot
      :collapsible="true"
      v-model="openItems"
      type="multiple"
      class="flex flex-col gap-2 grow"
    >
      <CollapsibleViewItem
        v-for="item in tasksCollapsibles"
        :key="item.status"
        :filter="filter"
        :ref="`${item.status}Section`"
        :status="item.status"
        @click:task="(task) => emit('show:detail', task)"
        @update:loading="handleUpdateLoading"
      />
    </AccordionRoot>
  </div>
</template>

<script setup>
import { ref, computed, inject } from "vue";
import { useI18n } from "vue-i18n";
import { AccordionRoot } from "radix-vue";
import CollapsibleViewItem from "@/SharedComponents/Passport/Steps/Tasks/Partials/CollapsibleViewItem.vue";

// Translations
const { t } = useI18n();

// Emitter
const emitter = inject("emitter");

// Emits
const emit = defineEmits([
  "show:detail",
  "update:count",
  "update:item",
  "update:fetchingStatus",
]);

// Define Props
const props = defineProps({
  item: { type: Object, required: true },
  count: { type: [Number, null], required: true },
  filter: { type: Object, required: true },
  fetchingStatus: { type: String, required: true },
});

// Refs
const openItems = ref(["open", "scheduled"]);

const tasksCollapsibles = ref([
  {
    title: t("admin.insights.tasks.open.title"),
    status: "open",
    fetchingStatus: "loading",
    count: null,
  },
  {
    title: t("admin.insights.tasks.scheduled.title"),
    status: "scheduled",
    fetchingStatus: "loading",
    count: null,
  },
  {
    title: t("admin.insights.tasks.completed.title"),
    status: "completed",
    fetchingStatus: "loading",
    count: null,
  },
]);

// Computed Props
const state = computed(() => {
  if (props.fetchingStatus === "loading") {
    return false;
  }

  return props.count === 0 || props.fetchingStatus === "error";
});

// Functions
const getTasksCollapsible = (status) => {
  return tasksCollapsibles.value.find(
    (tasksCollapsible) => tasksCollapsible.status === status
  );
};

const handleUpdateLoading = ({ status, fetchingStatus, count }) => {
  const tasksCollapsible = getTasksCollapsible(status);

  let item = props.item;

  switch (status) {
    case "open":
      item.tasks_open = count;
      break;
    case "scheduled":
      item.tasks_scheduled = count;
      break;
    case "completed":
      item.tasks_completed = count;
      break;
  }

  emitter.emit("update-assets-data", { data: [item] });

  if (tasksCollapsible) {
    tasksCollapsible.count = count;
    tasksCollapsible.fetchingStatus = fetchingStatus;
  }

  handleUpdateTaskCount(
    tasksCollapsibles.value.reduce(
      (acc, tasksCollapsible) => acc + tasksCollapsible.count,
      0
    )
  );

  handleUpdateFetchingStatus(
    tasksCollapsibles.value.map(
      (tasksCollapsible) => tasksCollapsible.fetchingStatus
    )
  );
};

const handleUpdateTaskCount = (count) => {
  emit("update:count", count);
};

const handleUpdateFetchingStatus = (fetchingStatuses) => {
  let state = "loading";

  if (fetchingStatuses.every((fetchingStatus) => fetchingStatus === "loaded")) {
    state = "loaded";
  }

  if (fetchingStatuses.some((fetchingStatus) => fetchingStatus === "error")) {
    state = "error";
  }

  emit("update:fetchingStatus", state);
};
</script>
